import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { ArrowLeft, RefreshCw, TrendingUp, Users, Activity, Award, AlertTriangle, BarChart2, Zap, Star, Sparkles, Flag } from 'lucide-react';
import api from '../api';
import Loader from '../components/Loader';
import './AdminReportsPage.css';

function AdminReportsPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [reportData, setReportData] = useState(null);
    const [isUpdatingReports, setIsUpdatingReports] = useState(false);
    const [reportFilters, setReportFilters] = useState({
        date_from: '',
        date_to: '',
        university_id: '',
        expert_id: ''
    });
    const [filterOptions, setFilterOptions] = useState({ universities: [], experts: [] });

    useEffect(() => {
        fetchReports();
        fetchFilterOptions();

        const intervalId = setInterval(() => {
            fetchReports();
        }, 60000);

        return () => clearInterval(intervalId);
    }, [reportFilters]);

    const fetchReports = async () => {
        try {
            const params = {};
            if (reportFilters.date_from) params.date_from = reportFilters.date_from;
            if (reportFilters.date_to) params.date_to = reportFilters.date_to;
            if (reportFilters.university_id) params.university_id = reportFilters.university_id;
            if (reportFilters.expert_id) params.expert_id = reportFilters.expert_id;

            const res = await api.get('/admin/reports', { params });
            setReportData(res.data);
        } catch (err) {
            console.error("Error fetching reports:", err);
        } finally {
            setIsUpdatingReports(false);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const res = await api.get('/admin/directory');
            const unis = res.data.filter(u => u.role === 'university').map(u => ({ id: u.id, name: u.details }));
            const experts = res.data.filter(u => u.role === 'pop').map(u => ({ id: u.id, name: u.details }));
            setFilterOptions({ universities: unis, experts });
        } catch (err) {
            console.error("Error fetching filter options:", err);
        }
    };

    const handleReportFilterChange = (field, value) => {
        setReportFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyReportFilters = () => {
        setIsUpdatingReports(true);
        fetchReports();
    };

    if (!reportData) return <Loader />;

    const sessionStatusData = [
        { name: 'Completed', value: reportData.completed_sessions },
        { name: 'Live', value: reportData.live_sessions },
        { name: 'Pending', value: reportData.pending_sessions },
        { name: 'Cancelled', value: reportData.cancelled_sessions },
        { name: 'No-Show', value: reportData.no_show_sessions },
    ];

    const sentimentData = [
        { name: 'Positive', value: reportData.feedback_sentiment.POSITIVE },
        { name: 'Neutral', value: reportData.feedback_sentiment.NEUTRAL },
        { name: 'Negative', value: reportData.feedback_sentiment.NEGATIVE },
    ];

    const renderOverview = () => (
        <div className="reports-tab-content">
            {/* Executive KPIs */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-info">
                        <span className="stat-label">TOTAL SESSIONS</span>
                        <span className="stat-value">{reportData.total_sessions}</span>
                        <span className={`stat-trend ${reportData.total_sessions > 0 ? 'positive' : ''}`}>
                            {reportData.total_sessions > 0 ? 'Active' : '-'}
                        </span>
                    </div>
                    <div className="stat-icon time"><BarChart2 size={24} /></div>
                </div>

                <div className="stat-card">
                    <div className="stat-info">
                        <span className="stat-label">EFFICIENCY</span>
                        <span className="stat-value">
                            {Math.round((reportData.completed_sessions / reportData.total_sessions) * 100 || 0)}%
                        </span>
                        <span className="stat-trend positive">Completion Rate</span>
                    </div>
                    <div className="stat-icon approved"><Zap size={24} /></div>
                </div>

                <div className="stat-card">
                    <div className="stat-info">
                        <span className="stat-label">NPS SCORE</span>
                        <span className="stat-value">
                            {reportData.avg_nps}
                        </span>
                        <span className="stat-trend">Avg Rating: {reportData.avg_rating}</span>
                    </div>
                    <div className="stat-icon pending"><Star size={24} /></div>
                </div>

                <div className="stat-card">
                    <div className="stat-info">
                        <span className="stat-label">PARTICIPATION</span>
                        <span className="stat-value">
                            {reportData.student_engagement[0]?.actual_participation || 0}
                        </span>
                        <span className="stat-trend positive">Students Engaged</span>
                    </div>
                    <div className="stat-icon declined"><Users size={24} /></div>
                </div>
            </div>

            {/* Charts Row */}
            <div className="admin-charts-layout">
                <div className="chart-container applications-section">
                    <div className="section-header"><h3>Session Lifecycle</h3></div>
                    <div style={{ padding: '20px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={sessionStatusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {[
                                        { name: 'Completed', color: '#10b981' },
                                        { name: 'Live', color: '#3b82f6' },
                                        { name: 'Pending', color: '#f59e0b' },
                                        { name: 'Cancelled', color: '#ef4444' },
                                        { name: 'No-Show', color: 'var(--text-muted)' }
                                    ].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="chart-container applications-section">
                    <div className="section-header"><h3>Feedback Sentiment</h3></div>
                    <div style={{ padding: '20px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={sentimentData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {[
                                        { name: 'Positive', color: '#10b981' },
                                        { name: 'Neutral', color: 'var(--text-muted)' },
                                        { name: 'Negative', color: '#ef4444' }
                                    ].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderExpertPerformance = () => (
        <div className="reports-tab-content">
            <div className="admin-charts-layout">
                <div className="chart-container applications-section" style={{ gridColumn: '1 / -1' }}>
                    <div className="section-header"><h3>Expert Performance</h3></div>
                    <div style={{ padding: '20px' }}>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={reportData.expert_performance}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} />
                                <Tooltip cursor={{ fill: 'var(--border)' }} />
                                <Legend />
                                <Bar dataKey="health_score" name="Health Score" radius={[4, 4, 0, 0]} barSize={20}>
                                    {reportData.expert_performance.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.health_score > 80 ? '#10b981' : entry.health_score > 60 ? '#f59e0b' : '#ef4444'} />
                                    ))}
                                </Bar>
                                <Bar dataKey="engagement_rate" name="Engagement Rate %" fill="#0891b2" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="applications-section table-container">
                <div className="section-header"><h3>Extended Audit Log</h3></div>
                <table className="admin-data-table">
                    <thead>
                        <tr>
                            <th>Expert Designation</th>
                            <th>Sessions</th>
                            <th>Engagement Rate</th>
                            <th>Cancellations</th>
                            <th>Health Index</th>
                            <th>Risk Level</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.expert_performance.map(exp => (
                            <tr key={exp.expert_id}>
                                <td className="font-bold">{exp.name}</td>
                                <td>{exp.total_sessions}</td>
                                <td>{exp.engagement_rate}%</td>
                                <td className="text-red">{exp.cancellation_rate}%</td>
                                <td>
                                    <span className={`health-badge ${exp.health_score > 80 ? 'high' : exp.health_score > 60 ? 'medium' : 'low'}`}>
                                        {exp.health_score}
                                    </span>
                                </td>
                                <td>
                                    <span className={`risk-badge ${exp.health_score > 80 ? 'low' : exp.health_score > 60 ? 'medium' : 'high'}`}>
                                        {exp.health_score > 80 ? 'Low' : exp.health_score > 60 ? 'Medium' : 'High'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderStudentEngagement = () => (
        <div className="reports-tab-content">
            <div className="applications-section table-container">
                <div className="section-header"><h3>Participation Metrics</h3></div>
                <table className="admin-data-table">
                    <thead>
                        <tr>
                            <th>Total Registrations</th>
                            <th>Active Participation</th>
                            <th>Estimated Duration</th>
                            <th>Engagement Score</th>
                            <th>Unique Students</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.student_engagement.map((eng, idx) => (
                            <tr key={idx}>
                                <td>{eng.total_registrations || eng.total_unique_students}</td>
                                <td className="text-green">{eng.actual_participation}</td>
                                <td>{eng.avg_time_attended_min} min (est)</td>
                                <td>
                                    <span className={`engagement-badge ${eng.engagement_score > 75 ? 'high' : eng.engagement_score > 50 ? 'medium' : 'low'}`}>
                                        {eng.engagement_score}%
                                    </span>
                                </td>
                                <td>{eng.total_unique_students}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {reportData.engagement_heatmap && reportData.engagement_heatmap.length > 0 && (
                <div className="chart-container applications-section" style={{ marginTop: '20px' }}>
                    <div className="section-header"><h3>Engagement Heatmap</h3></div>
                    <div style={{ padding: '20px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={reportData.engagement_heatmap}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="time_slot" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="engagement" stroke="#0891b2" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );

    const renderUniversityAnalytics = () => (
        <div className="reports-tab-content">
            <div className="applications-section table-container">
                <div className="section-header"><h3>University Analytics</h3></div>
                <table className="admin-data-table">
                    <thead>
                        <tr>
                            <th>University</th>
                            <th>Sessions</th>
                            <th>Enrollment Rate</th>
                            <th>Student Growth</th>
                            <th>Satisfaction</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.university_stats.map(uni => (
                            <tr key={uni.uni_id}>
                                <td className="font-bold">{uni.name}</td>
                                <td>{uni.sessions}</td>
                                <td className="text-green">{uni.enrollment_rate}%</td>
                                <td>
                                    <span className={`growth-badge ${uni.student_growth > 0 ? 'positive' : 'negative'}`}>
                                        {uni.student_growth > 0 ? '+' : ''}{uni.student_growth}%
                                    </span>
                                </td>
                                <td>{uni.avg_satisfaction || 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderInsights = () => (
        <div className="reports-tab-content">
            <div className="insights-grid">
                <div className="sidebar-section ai-insights-panel applications-section">
                    <div className="section-header"><h3><Sparkles size={20} style={{ marginRight: '8px', color: '#6366f1', verticalAlign: 'text-bottom' }} /> AI Operational Insights</h3></div>
                    <div className="ai-insight-list" style={{ padding: '20px' }}>
                        {reportData.ai_insights.length === 0 ? (
                            <p className="ai-placeholder">No significant patterns detected in current period.</p>
                        ) : (
                            reportData.ai_insights.map((insight, idx) => (
                                <div key={idx} className="ai-insight-item">
                                    <span className="ai-pulse"></span>
                                    <p>{insight}</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="sidebar-section anomalies-panel applications-section">
                    <div className="section-header"><h3><Flag size={20} style={{ marginRight: '8px', color: '#ef4444', verticalAlign: 'text-bottom' }} /> Anomaly Detection</h3></div>
                    <div className="anomaly-list" style={{ padding: '20px' }}>
                        {reportData.anomalies.length === 0 ? (
                            <p className="anomaly-placeholder">Systems within normal thresholds.</p>
                        ) : (
                            reportData.anomalies.map((anom, idx) => (
                                <div key={idx} className={`anomaly-item ${anom.type.toLowerCase()}`}>
                                    <div className="anomaly-header">
                                        <span className="anomaly-type">{anom.type}</span>
                                        <span className="anomaly-entity">{anom.entity}</span>
                                    </div>
                                    <p className="anomaly-reason">{anom.reason}</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="vetting-layout">
            {/* Sidebar Navigation */}
            <aside className="vetting-sidebar">
                <div style={{ padding: '0 20px 20px 0 20px' }}>
                    {/* Spacer if needed or Logo */}
                </div>
                <nav className="sidebar-nav" style={{ padding: '0 10px' }}>
                    <button
                        className={`sidebar-item ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <span className="sidebar-icon"><TrendingUp size={18} /></span>
                        Overview
                    </button>
                    <button
                        className={`sidebar-item ${activeTab === 'experts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('experts')}
                    >
                        <span className="sidebar-icon"><Award size={18} /></span>
                        Expert Performance
                    </button>
                    <button
                        className={`sidebar-item ${activeTab === 'students' ? 'active' : ''}`}
                        onClick={() => setActiveTab('students')}
                    >
                        <span className="sidebar-icon"><Users size={18} /></span>
                        Student Engagement
                    </button>
                    <button
                        className={`sidebar-item ${activeTab === 'universities' ? 'active' : ''}`}
                        onClick={() => setActiveTab('universities')}
                    >
                        <span className="sidebar-icon"><Activity size={18} /></span>
                        University Analytics
                    </button>
                    <button
                        className={`sidebar-item ${activeTab === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveTab('insights')}
                    >
                        <span className="sidebar-icon"><AlertTriangle size={18} /></span>
                        AI Insights
                    </button>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="vetting-main">
                <header className="vetting-topbar">
                    <div className="topbar-title">
                        <h1>Reports & Analytics</h1>
                    </div>
                    <div className="topbar-actions">
                        <button
                            className={`admin-refresh-btn ${isUpdatingReports ? 'button-loading' : ''}`}
                            onClick={applyReportFilters}
                            disabled={isUpdatingReports}
                        >
                            {isUpdatingReports ? 'Syncing...' : <><RefreshCw size={18} aria-hidden="true" /> Refresh Data</>}
                        </button>
                        <Link to="/admin-dashboard" className="admin-dashboard-btn">
                            <ArrowLeft size={18} style={{ marginRight: '8px' }} /> Admin Dashboard
                        </Link>
                    </div>
                </header>

                <div className="vetting-content">
                    {/* Filter Bar */}
                    <div className="reports-filter-bar">
                        <div className="filter-group">
                            <label className="filter-label">Date Range</label>
                            <div className="filter-date-inputs">
                                <input type="date" className="filter-input" value={reportFilters.date_from} onChange={(e) => handleReportFilterChange('date_from', e.target.value)} />
                                <span>to</span>
                                <input type="date" className="filter-input" value={reportFilters.date_to} onChange={(e) => handleReportFilterChange('date_to', e.target.value)} />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">University</label>
                            <select className="filter-select" value={reportFilters.university_id} onChange={(e) => handleReportFilterChange('university_id', e.target.value)}>
                                <option value="">All Institutions</option>
                                {filterOptions.universities.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Expert</label>
                            <select className="filter-select" value={reportFilters.expert_id} onChange={(e) => handleReportFilterChange('expert_id', e.target.value)}>
                                <option value="">All Experts</option>
                                {filterOptions.experts.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                        </div>

                        <button className="filter-apply-btn" onClick={applyReportFilters}>
                            Apply Filters
                        </button>
                    </div>

                    {/* Content Renderers */}
                    {activeTab === 'overview' && renderOverview()}
                    {activeTab === 'experts' && renderExpertPerformance()}
                    {activeTab === 'students' && renderStudentEngagement()}
                    {activeTab === 'universities' && renderUniversityAnalytics()}
                    {activeTab === 'insights' && renderInsights()}
                </div>
            </main>
        </div>
    );
}

export default AdminReportsPage;
